
import { supabase } from "@/integrations/supabase/client";
import { debugLog, DebugLevel } from "@/utils/debugUtils";
import { updateGameStateAfterSubmission } from "@/utils/gameStateUpdates";
import { validateRoundWordData, verifyRoundExists, updateRoundData } from "./databaseOperations";

/**
 * Processes both user and AI words to update game state
 */
export const processBothWords = async (
  gameId: string,
  roundNumber: number,
  userWord: string,
  aiWord: string,
  aiExplanation: string | undefined,
  usedWords: Set<string>,
  setIsSubmitting: (isSubmitting: boolean) => void,
  stateSetters: {
    setRounds: (rounds: any) => void,
    setUsedWords: (usedWords: any) => void,
    setCurrentWord: (word: string) => void,
    setIsRevealed: (isRevealed: boolean) => void,
    setBotHasChosenWord: (hasChosen: boolean) => void,
    setBotIsThinking: (isThinking: boolean) => void,
    setShouldAutoFocus: (shouldAutoFocus: boolean) => void,
    setGameOver: (gameOver: boolean) => void,
    setConvergenceRound: (round: number | null) => void,
    setGameStatus: (status: string) => void,
    setUserHasSubmitted: (hasSubmitted: boolean) => void,
    setProcessingComplete: (complete: boolean) => void
  },
  toast: any
) => {
  if (!gameId) {
    debugLog(DebugLevel.ERROR, "wordProcessing", "Cannot process both words: gameId missing");
    return;
  }
  
  debugLog(DebugLevel.INFO, "wordProcessing", `Processing round ${roundNumber} with: User word: ${userWord}, Bot word: ${aiWord}`);
  
  try {
    // Ensure the round exists in the database
    const roundExists = await verifyRoundExists(gameId, roundNumber);
    if (!roundExists) {
      debugLog(DebugLevel.ERROR, "wordProcessing", `Failed to verify round ${roundNumber} exists`);
      toast({
        variant: "destructive",
        title: "Error",
        description: "There was a problem with the round data."
      });
      setIsSubmitting(false);
      stateSetters.setProcessingComplete(true);
      return;
    }
    
    // Sync database with the current words if needed
    if (userWord || aiWord) {
      await updateRoundData(
        gameId,
        roundNumber,
        userWord || null,
        aiWord || null,
        aiExplanation || null
      );
    }
    
    // Validate the word data
    const validatedData = await validateRoundWordData(gameId, roundNumber, userWord, aiWord);
    if (!validatedData.success) {
      debugLog(DebugLevel.ERROR, "wordProcessing", `Failed to validate word data for round ${roundNumber}`);
      toast({
        variant: "destructive",
        title: "Error",
        description: "There was a problem with the words for this round."
      });
      setIsSubmitting(false);
      stateSetters.setProcessingComplete(true);
      return;
    }
    
    // Use validated words
    const finalUserWord = validatedData.userWord;
    const finalAiWord = validatedData.aiWord;
    const finalAiExplanation = validatedData.aiExplanation;
    
    debugLog(DebugLevel.INFO, "wordProcessing", `Final validated words - User: ${finalUserWord}, Bot: ${finalAiWord}`);
    
    // Set critical state values
    stateSetters.setBotHasChosenWord(true);
    stateSetters.setUserHasSubmitted(true);
    
    // Process the round with validated data
    await updateGameStateAfterSubmission(
      gameId,
      roundNumber,
      finalUserWord,
      finalAiWord,
      finalAiExplanation,
      usedWords,
      stateSetters.setRounds,
      stateSetters.setUsedWords,
      stateSetters.setCurrentWord,
      stateSetters.setIsRevealed,
      stateSetters.setBotHasChosenWord,
      stateSetters.setBotIsThinking,
      stateSetters.setShouldAutoFocus,
      stateSetters.setGameOver,
      stateSetters.setConvergenceRound,
      stateSetters.setGameStatus,
      stateSetters.setUserHasSubmitted,
      stateSetters.setProcessingComplete,
      toast,
      setIsSubmitting  // Pass the setIsSubmitting function
    );
    
    debugLog(DebugLevel.INFO, "wordProcessing", `Successfully processed round ${roundNumber}`);
  } catch (error) {
    debugLog(DebugLevel.ERROR, "wordProcessing", "Error processing both words:", error);
    toast({
      variant: "destructive",
      title: "Error",
      description: "There was a problem processing the words."
    });
    
    // Reset states on error
    stateSetters.setBotIsThinking(false);
    stateSetters.setUserHasSubmitted(false);
    stateSetters.setProcessingComplete(true);
  } finally {
    setIsSubmitting(false);
  }
};
